import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { cccdAdmin, type AdminEvent, type AdminRegionalEvent } from '@/lib/api/cccdAdminClient';
import { TeamShell } from '@/components/cccd/TeamShell';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import Alert from '@/components/ui/alert/Alert';
import { inputCls, labelCls, textareaCls, StyledSelect, btnSave, btnSecondary } from '@/components/admin/FormComponents';
import { toastError } from '@/lib/toast';

type InstitutionOption = { id: string; name: string };

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '—';
}

// La creación de equipo ya no ofrece un selector de Evento: el padre (evento
// o regional) siempre llega por el contexto de la ruta desde donde se abrió
// "Nuevo equipo" (cccd-admin-tabbed-shell — "el padre de un hijo se infiere
// del contexto de ruta").
export default function TeamCreatePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('eventId') ?? '';
  const regionalId = searchParams.get('regionalId') ?? '';
  const parentId = eventId || regionalId;

  const [parentEvent, setParentEvent] = useState<AdminEvent | AdminRegionalEvent | null>(null);
  const [institutions, setInstitutions] = useState<InstitutionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    captainEmail: '',
    captainName: '',
    name: '',
    categoryId: '',
    planId: '',
    institutionId: '',
    initialStatus: 'draft',
    negotiationNotes: '',
  });

  useEffect(() => {
    if (eventId) cccdAdmin.getEvent(eventId).then(setParentEvent).catch(() => setParentEvent(null));
    else if (regionalId) cccdAdmin.getRegional(regionalId).then(setParentEvent).catch(() => setParentEvent(null));
    api
      .get('/academic-institutions/admin?limit=200&page=1')
      .then((res) => setInstitutions(res.data?.data?.institutions ?? []))
      .catch(() => setInstitutions([]));
  }, [eventId, regionalId]);

  const { data: categories = [], isFetching: loadingCategories } = useQuery({
    queryKey: ['event-categories', eventId, regionalId],
    queryFn: () => (eventId ? cccdAdmin.getEventCategories(eventId) : cccdAdmin.getRegionalCategories(regionalId)),
    enabled: !!parentId,
  });

  const { data: plans = [], isFetching: loadingPlans } = useQuery({
    queryKey: ['event-plans', eventId, regionalId],
    queryFn: () => (eventId ? cccdAdmin.getEventPlans(eventId) : cccdAdmin.getRegionalPlans(regionalId)),
    enabled: !!parentId,
  });

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const f = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      set(key, e.target.value),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!parentId) return;
    setLoading(true);
    setError('');
    try {
      const team = await cccdAdmin.createTeam({
        ...form,
        // Opcional (cccd-team-optional-captain): mandar el par completo o
        // ninguno — el backend rechaza uno solo de los dos.
        captainEmail: form.captainEmail.trim() || undefined,
        captainName: form.captainName.trim() || undefined,
        eventId: eventId || undefined,
        regionalEventId: regionalId || undefined,
        institutionId: form.institutionId || undefined,
        addons: [],
      });
      qc.invalidateQueries({ queryKey: ['teams'] });
      navigate(`/cccd/teams/${team.id}`);
    } catch (err: any) {
      const message = err?.response?.data?.error ?? 'Error al crear el equipo.';
      setError(message);
      toastError(message);
      setLoading(false);
    }
  };

  const noCategories = !!parentId && !loadingCategories && categories.length === 0;
  const noPlans = !!parentId && !loadingPlans && plans.length === 0;
  const selectedCategory = categories.find((c) => c.slug === form.categoryId);
  const selectedInstitution = institutions.find((i) => i.id === form.institutionId);

  if (!parentId) {
    return (
      <TeamShell
        mode="create"
        activeTab="info"
        onTabChange={() => {}}
        breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Crear equipo']}
        title="Crear equipo"
        subtitle="Falta el evento o regional de origen"
      >
        <Alert variant="error" title="No se puede crear el equipo" message="Abre 'Nuevo equipo' desde la pestaña Equipos de un evento o regional." />
      </TeamShell>
    );
  }

  return (
    <TeamShell
      mode="create"
      activeTab="info"
      onTabChange={() => {}}
      breadcrumbs={[
        { label: 'SateLab', to: '/' },
        { label: 'CCCD', to: '/cccd' },
        { label: parentEvent?.name ?? '...', to: eventId ? `/cccd/events/${eventId}` : `/cccd/regionals/${regionalId}` },
        'Crear equipo',
      ]}
      title="Crear equipo desde backoffice"
      subtitle={parentEvent ? `Registrando un equipo para ${parentEvent.name}` : 'Registra un equipo manualmente, con o sin pago inicial'}
    >
      <div className="flex items-start gap-6">
        <form onSubmit={handleSubmit} className="min-w-0 flex-1 space-y-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SectionHeader
            title="Categoría y plan"
            subtitle={parentEvent ? `El evento ya es este — ${parentEvent.name} — solo falta la categoría` : 'Categoría y plan de pago del equipo'}
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22V4a2 2 0 0 1 2-2h9l5 5v15" /><path d="M15 2v5h5" /></svg>}
          >
            {noCategories && (
              <Alert
                variant="warning"
                title="Sin categorías configuradas"
                message='Este evento no tiene categorías configuradas. Ve a la pestaña "Configuración" para añadirlas.'
              />
            )}
            {noPlans && (
              <Alert
                variant="warning"
                title="Sin planes de pago configurados"
                message='Este evento no tiene planes de pago configurados. Ve a la pestaña "Configuración" para añadirlos.'
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Categoría *</label>
                <select
                  required
                  className={`${inputCls} disabled:opacity-50`}
                  disabled={loadingCategories}
                  {...f('categoryId')}
                >
                  <option value="">{loadingCategories ? 'Cargando...' : 'Seleccionar categoría...'}</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Plan de pago *</label>
                <select
                  required
                  className={`${inputCls} disabled:opacity-50`}
                  disabled={loadingPlans}
                  {...f('planId')}
                >
                  <option value="">{loadingPlans ? 'Cargando...' : 'Seleccionar plan...'}</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.slug}>{plan.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </SectionHeader>

          <SectionHeader
            title="Equipo"
            subtitle="Nombre e institución de origen"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21c1-4 4-6 9-6s8 2 9 6" /><circle cx="12" cy="7" r="4" /></svg>}
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nombre del equipo *</label>
                <input required className={inputCls} {...f('name')} />
              </div>
              <div>
                <label className={labelCls}>Institución académica</label>
                <StyledSelect value={form.institutionId} onChange={(e) => set('institutionId', e.target.value)}>
                  <option value="">Sin institución</option>
                  {institutions.map((inst) => (
                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                  ))}
                </StyledSelect>
              </div>
            </div>
          </SectionHeader>

          <SectionHeader
            title="Responsable"
            subtitle="Opcional — también se puede elegir después desde Miembros"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 20c1.5-4 5-6 8-6s6.5 2 8 6" /></svg>}
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nombre completo</label>
                <input className={inputCls} {...f('captainName')} />
              </div>
              <div>
                <label className={labelCls}>Correo</label>
                <input type="email" className={inputCls} {...f('captainEmail')} />
              </div>
            </div>
            <p className="mt-2.5 text-xs text-gray-500 dark:text-gray-400">Si el email no existe, se creará la cuenta automáticamente. Déjalo en blanco para asignar el responsable más tarde desde el tab Miembros.</p>
          </SectionHeader>

          <SectionHeader
            title="Estado inicial"
            subtitle="Solo al crear — después, el estado se cambia desde la pestaña Negociación"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22V3" /></svg>}
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Estado</label>
                <select className={inputCls} {...f('initialStatus')}>
                  <option value="draft">Borrador</option>
                  <option value="negotiating">Negociando</option>
                  <option value="payment_pending">Pago pendiente</option>
                  <option value="active">Activo (beca / pago externo)</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className={labelCls}>Notas internas</label>
              <textarea rows={2} className={textareaCls} {...f('negotiationNotes')} />
            </div>
          </SectionHeader>
        </div>

          {error && <Alert variant="error" title="No se pudo crear el equipo" message={error} />}

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => navigate(eventId ? `/cccd/events/${eventId}` : `/cccd/regionals/${regionalId}`)} className={btnSecondary}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} className={btnSave}>
              {loading ? 'Creando...' : 'Crear equipo'}
            </button>
          </div>
        </form>

        <div className="sticky top-6 hidden w-[340px] shrink-0 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:block">
          <div className="mb-4 flex items-center gap-2">
            <span className="size-[7px] rounded-full bg-success-500 shadow-[0_0_0_4px_rgba(18,183,106,0.15)]" />
            <span className="text-xs font-semibold uppercase tracking-wide text-success-700 dark:text-success-400">Resumen en vivo</span>
          </div>
          <p className="text-base font-bold text-gray-900 dark:text-white">{form.name || 'Nombre del equipo'}</p>
          <p className="mt-0.5 text-[13px] text-gray-500 dark:text-gray-400">
            {parentEvent?.name ?? '—'}{selectedCategory ? ` · Categoría ${selectedCategory.name}` : ''}
          </p>

          <div className="mt-3.5 border-t border-gray-100 pt-3.5 dark:border-gray-800">
            <div className="flex items-baseline justify-between">
              <span className="text-[13px] font-semibold text-gray-800 dark:text-white/90">Precio de categoría</span>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                {selectedCategory ? `$${selectedCategory.basePriceCop.toLocaleString('es-CO')}` : '—'}
              </span>
            </div>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Sin descuento — se define en Negociación una vez creado el equipo</p>
          </div>

          {form.captainName && (
            <div className="mt-4 flex items-center gap-2 rounded-[10px] bg-gray-50 p-2.5 dark:bg-gray-800">
              <div className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {initials(form.captainName)}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{form.captainName}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">{form.captainEmail || '—'}</p>
              </div>
            </div>
          )}

          {selectedInstitution && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Institución: {selectedInstitution.name}</p>
          )}
        </div>
      </div>
    </TeamShell>
  );
}
